<template>
  <v-card
    class="rounded-lg fill-height"
    :height="height ? height : null"
  >
    <v-card-title class="ml-2">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.diagnostics_trend') }}
          </div>
        </v-col>
        <template v-if="!removable">
          <v-col cols="auto">
            <v-select
              v-model="diagnosticProtocols.selected"
              solo
              return-object
              :items="diagnosticProtocols.data"
              dense
              item-value="name"
              item-text="name"
              :label="$t('dropdown.select_protocol.title')"
              hide-details
              style="width: 300px"
            />
          </v-col>
          <v-col
            class="ml-2"
            cols="auto"
          >
            <v-select
              v-model="variables.selected"
              solo
              multiple
              chips
              small-chips
              deletable-chips
              return-object
              :items="variables.data"
              dense
              item-value="var_id"
              item-text="var_name"
              :label="$t('dropdown.select_variables_to_show.title')"
              hide-details
              style="width: 300px"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="index === 0"
                  class="text-grey text-caption align-self-center"
                >
                  {{ $tc("dropdown.number_of_selected_variables", variables.selected.length, {num: variables.selected.length}) }}
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col
            cols="auto"
            class="ml-2"
          >
            <v-btn-toggle
              v-model="dateRange"
              color="primary"
              mandatory
            >
              <v-btn
                small
                :value="1"
              >
                {{ $t('button.month_one') }}
              </v-btn>
              <v-btn
                small
                :value="3"
              >
                {{ $t('button.month_three') }}
              </v-btn>
              <v-btn
                small
                :value="6"
              >
                {{ $t('button.month_six') }}
              </v-btn>
              <v-btn
                small
                :value="12"
              >
                {{ $t('button.year_one') }}
              </v-btn>
              <v-btn
                small
                :value="60"
              >
                {{ $t('button.year_five') }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col
            cols="auto"
            class="ml-2"
          >
            <v-btn-toggle
              v-model="view"
              color="primary"
              mandatory
            >
              <v-btn
                small
                class="text-none"
                value="visual"
              >
                {{ $t('card.display_option.visual') }}
              </v-btn>
              <v-btn
                small
                class="text-none"
                value="list"
              >
                {{ $t('card.display_option.list') }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </template>
        <v-col
          v-if="removable"
          cols="auto"
          class="ml-auto"
        >
          <v-btn
            icon
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text
      v-if="view === 'list' && !removable"
    >
      <v-data-table
        :headers="headers"
        :items="rawData"
        :loading="stats.loading"
        fixed-header
        :items-per-page="50"
        hide-default-footer
        dense
        height="400"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              {{ $dayjs(item.start_at).format('DD.MM.YYYY.') }}
            </td>
            <td
              v-for="variable in variables.selected"
              :key="variable.var_id"
              class="font-weight-bold"
            >
              {{ item[variable.var_id] }} {{ variable.unit }}
              <v-icon
                v-if="item[`${variable.var_id}_trend`]"
                :color="item[`${variable.var_id}_trend`] > 0 ? 'success' : item[`${variable.var_id}_trend`] === 0 ? 'warning' : 'error'"
              >
                {{ item[`${variable.var_id}_trend`] > 0 ? 'fal fa-arrow-trend-up' : item[`${variable.var_id}_trend`] === 0 ? 'fal horizontal-rule' : 'fal fa-arrow-trend-down' }}
              </v-icon>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-grey text-caption align-self-center">
            {{ $t('card.diagnostics_trend.select_protocol.hint') }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text v-else>
      <div
        v-if="!removable && (diagnosticProtocols.loading || stats.loading)"
        class="d-flex justify-center pt-12"
      >
        <v-progress-circular
          indeterminate
          size="150"
          width="8"
          color="grey lighten-2"
        />
      </div>
      <line-chart
        v-else
        :raw-data="rawData"
        x-value="start_at"
        :chart-value-keys="chartValueKeys"
        class="elevation-0"
        :xaxis="{
          type: 'datetime',
          tickPlacement: 'between',
          min: dayjs(Math.min(...rawData.map(d => dayjs(d.start_at).valueOf()))).subtract(1, 'day').valueOf(),
          max: dayjs(Math.max(...rawData.map(d => dayjs(d.start_at).valueOf()))).add(1, 'day').valueOf()
        }"
        :tooltip="{
          y: {
            formatter: (value, { seriesIndex, w }) => {
              if (value === null || value === undefined) return 'N/A';
              const series = w.config.series[seriesIndex];
              const seriesKey = series && series.name ? series.name : '';
              const chartKey = chartValueKeys.find(k => k.label === seriesKey);
              let unit = '';
              if (chartKey && chartKey.unit !== null && chartKey.unit !== undefined) {
                unit = chartKey.unit;
              }
              return `${value} ${unit}`.trim();
            }
          }
        }"
        :legend="{showForSingleSeries: true}"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import i18n from '@/plugins/i18n'
import { DiagnosticProtocolApiService, PlayerApiService } from '@/data/services'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'

export default {
  name: 'PlayerDiagnosticTrendChart',
  components: { LineChart },
  props: {
    player: {
      type: Object,
      default: () => ({})
    },
    protocolId: {
      type: Number,
      default: null
    },
    date: {
      type: Object,
      default: () => dayjs()
    },
    height: {
      type: Number,
      default: 500
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    stats: {
      data: [],
      loading: false
    },
    diagnosticProtocols: {
      loading: false,
      data: [],
      selected: undefined
    },
    variables: {
      data: [],
      selected: [],
      available: [],
      loading: false
    },
    view: 'visual',
    dateRange: 3,
    eventsCount: null
  }),
  computed: {
    headers() {
      const headers = [
        { text: i18n.t('picker.date.title'), value: 'date', width: '15%' }
      ]

      for (const variable of this.variables.selected) {
        headers.push({ text: variable.var_name, value: variable.value })
      }

      return headers
    },
    chartValueKeys() {
      return this.variables.selected.map(variable => {
        return { key: variable.var_id, type: 'line', label: variable.var_name, unit: variable.unit }
      })
    },
    rawData() {
      return this.stats.data.map((stat, index) => {
        const data = { start_at: dayjs(stat.event.start_at).format('YYYY-MM-DD') }

        for (const variable of this.variables.selected) {
          const statVariable = stat.variables.find(v => v.var_id === variable.var_id)
          data[variable.var_id] = statVariable ? statVariable.value : null

          if (index < this.stats.data.length - 1) {
            const nextStat = this.stats.data[index + 1]
            const nextStatVariable = nextStat.variables.find(v => v.var_id === variable.var_id)

            if (statVariable && nextStatVariable) {
              data[`${variable.var_id}_trend`] = statVariable.value - nextStatVariable.value
            } else {
              data[`${variable.var_id}_trend`] = null
            }
          }
        }

        return data
      })
    }
  },
  watch: {
    'variables.selected': function () {
      this.fetchDiagnosticProtocolPlayerStatsDebouced()
    },
    'diagnosticProtocols.selected': function () {
      if (!this.removable) this.fetchDiagnosticProtocolPlayerVariables()
    },
    dateRange: function () {
      this.fetchDiagnosticProtocolPlayerVariables()
    },
    protocolId: function (val) {
      this.diagnosticProtocols.selected = this.diagnosticProtocols.data.find(p => p.id === val)
    }
  },
  created() {
    if (this.removable) {
      this.setupMockData()
    } else {
      this.fetchDiagnosticProtocols()
    }
  },
  methods: {
    dayjs,
    setupMockData() {
      this.diagnosticProtocols.selected = { id: 1, name: 'Morning Assessment' }

      this.variables.data = [
        { var_id: 'HR_REST', var_name: 'Resting Heart Rate', unit: 'bpm' },
        { var_id: 'SLEEP_QUAL', var_name: 'Sleep Quality', unit: '/10' },
        { var_id: 'FATIGUE', var_name: 'Fatigue Level', unit: '/10' }
      ]
      this.variables.selected = this.variables.data.slice(0, 3)

      const mockStats = []
      for (let i = 0; i < 30; i++) {
        const date = dayjs(this.date).subtract(i, 'day')
        mockStats.push({
          event: {
            start_at: date.format('YYYY-MM-DD')
          },
          variables: [
            { var_id: 'HR_REST', value: Math.floor(Math.random() * 20) + 60 },
            { var_id: 'SLEEP_QUAL', value: Math.floor(Math.random() * 4) + 6 },
            { var_id: 'FATIGUE', value: Math.floor(Math.random() * 6) + 2 }
          ]
        })
      }
      this.stats.data = mockStats.reverse()
    },
    async fetchDiagnosticProtocolPlayerStats() {
      if (!this.diagnosticProtocols.selected) return

      this.stats.loading = true

      try {
        const params = {
          date_from: dayjs(this.date).subtract(this.dateRange, 'month').format('YYYY-MM-DD'),
          date_to: dayjs(this.date).format('YYYY-MM-DD')
        }

        const availableVarIds = this.variables.selected.map(v => v.var_id)

        if (availableVarIds.length > 0) params.vars = availableVarIds

        this.stats.data = await PlayerApiService.getDiagnosticProtocolPlayerStats(this.player.id, this.diagnosticProtocols.selected.id, params)
      } catch (e) {
        this.stats.data = []
        throw e
      } finally {
        this.stats.loading = false
      }
    },
    fetchDiagnosticProtocolPlayerStatsDebouced: debounce(function () {
      this.fetchDiagnosticProtocolPlayerStats()
    }, 200),
    async fetchDiagnosticProtocols() {
      if (this.removable) {
        this.setupMockData()
        return
      }

      try {
        this.diagnosticProtocols.loading = true

        const result = await DiagnosticProtocolApiService.getAll({ order_by: 'name', player_id: this.$route.params.id })

        this.diagnosticProtocols.data = result.data
        this.diagnosticProtocols.selected = this.protocolId ? result.data.find(p => p.id === this.protocolId) : result.data[0]
        this.fetchDiagnosticProtocolPlayerVariables()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.diagnosticProtocols.loading = false
      }
    },
    async fetchDiagnosticProtocolPlayerVariables() {
      if (!this.diagnosticProtocols.selected) return

      this.variables.loading = true

      const params = {
        date_from: dayjs(this.date).subtract(this.dateRange, 'month').format('YYYY-MM-DD'),
        date_to: dayjs(this.date).format('YYYY-MM-DD')
      }

      try {
        const result = await PlayerApiService.getDiagnosticProtocolPlayerVariables(this.player.id, this.diagnosticProtocols.selected.id, params)

        this.variables.data = result.variables
        this.eventsCount = result.events_count

        this.variables.selected = this.variables.data.slice(0, 5)
        this.view = this.eventsCount === 1 ? 'list' : 'view'
        this.fetchDiagnosticProtocolPlayerStatsDebouced()
      } catch (e) {
        this.variables.data = []
      } finally {
        this.variables.loading = false
      }
    }
  }
}
</script>
